/**
 * databaseSpec — Direct DB connectors (PostgreSQL, MySQL, SQL Server,
 * Oracle, SQLite, Generic ODBC). PR-INT-1: closes the V1 → V2 parity
 * gap for the "Direct DB" enterprise category — V1 shipped the six
 * engines via ``EnterpriseHubPage`` + ``ConnectorWizard``, V2 had nothing.
 *
 * v1 source-of-truth:
 *   ando-frontend/app/pages/EnterpriseHubPage.tsx (lines 314-333 = tile)
 *   ando-frontend/app/components/enterprise/ConnectorWizard.tsx
 *   ando-frontend/app/lib/connectors.ts (DB_CONNECTOR_KEYS)
 *
 * BE surface (already shipped):
 *   POST /api/v1/enterprise/connectors/db — accepts host/port/database/
 *   user/password/connector_type/connector_name.
 *
 * Per-vendor field set (variation from a uniform host/port/db form):
 *   - postgresql / mysql / sql_server / oracle → host/port/db/user/pass
 *   - sqlite                                    → file_path only
 *   - generic_odbc                              → dsn + connection_string
 *
 * SSL field intentionally NOT exposed (June 10, 2026 — user feedback:
 * "togli SSL che tanto non la uso"). BE default is ``prefer`` which
 * upgrades to TLS when the server offers it and falls back otherwise —
 * matches every supported managed PG/MySQL provider. If a hard
 * "require" is ever needed, re-introduce a single field here (no
 * BE-side migration needed — column is already there).
 *
 * The dialog selects the matching credentials step via the new
 * ``WizardStepSpec.vendor`` discriminator (added with this PR) — no
 * per-vendor capability slug needed, ``database`` stays single-slug.
 */

import type { BaseConnection, CapabilityWizardSpec, FieldSpec } from "@ando/ui";
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Database, Layers, RefreshCw, Search, Sparkles } from "lucide-react";

import { createCapabilityApi } from "../api/capabilityApi";
import { vendorIcon } from "../components/vendorIcons";

export interface DatabaseConnection extends BaseConnection {
  readonly connector_type?: string;
  readonly host?: string;
  readonly database?: string;
  readonly file_path?: string;
  readonly dsn?: string;
}

const iconCls = "h-4 w-4";

// ── Field-set factories — DRY across the 4 host/port-style engines ─────
// Each engine differs only by default port; keep the rest of the shape in
// one place so adding e.g. CockroachDB later is a one-line addition.
function hostPortFields(defaultPort: number): readonly FieldSpec[] {
  return [
    {
      name: "host",
      label: "Host",
      type: "string",
      required: true,
      placeholder: "localhost / db.example.com",
    },
    {
      name: "port",
      label: msg`Porta`,
      type: "number",
      required: true,
      placeholder: String(defaultPort),
    },
    {
      name: "database",
      label: msg`Nome database`,
      type: "string",
      required: true,
      placeholder: "production",
    },
    {
      name: "user",
      label: msg`Utente`,
      type: "string",
      required: true,
      placeholder: "ando_readonly",
    },
    {
      name: "password",
      label: "Password",
      type: "password",
      required: true,
    },
  ];
}

// ── SQLite — file-only field-set ─────────────────────────────────────────
const sqliteFields: readonly FieldSpec[] = [
  {
    name: "file_path",
    label: msg`Percorso file`,
    type: "string",
    required: true,
    placeholder: "/var/lib/ando/data.sqlite",
    helperText: msg`Percorso assoluto al file .sqlite/.db nel filesystem dell'agente.`,
  },
];

// ── ODBC — DSN + raw connection string ───────────────────────────────────
const odbcFields: readonly FieldSpec[] = [
  {
    name: "dsn",
    label: "DSN",
    type: "string",
    required: false,
    placeholder: "MyOdbcDSN",
    helperText: msg`Nome DSN registrato sull'agente — opzionale se compili la stringa sotto.`,
  },
  {
    name: "connection_string",
    label: msg`Stringa di connessione`,
    type: "string",
    required: false,
    placeholder: "Driver={ODBC Driver 18 for SQL Server};Server=...;Uid=...;Pwd=...;",
    helperText: msg`Stringa ODBC completa, alternativa al DSN.`,
  },
  {
    name: "user",
    label: msg`Utente`,
    type: "string",
    required: false,
  },
  {
    name: "password",
    label: "Password",
    type: "password",
    required: false,
  },
];

export const databaseSpec: CapabilityWizardSpec<DatabaseConnection> = {
  capability: "database",
  title: msg`Database SQL`,
  subtitle: "PostgreSQL, MySQL, SQL Server, Oracle, SQLite, ODBC",
  icon: <Database className="h-5 w-5" aria-hidden />,
  gdprDeleteWindowMinutes: 30,

  vendors: [
    {
      id: "postgresql",
      label: "PostgreSQL",
      icon: vendorIcon("postgresql", iconCls),
      description: msg`Database open-source standard de-facto`,
    },
    {
      id: "mysql",
      label: "MySQL",
      icon: vendorIcon("mysql", iconCls),
      description: msg`Open-source, ampia adozione`,
    },
    {
      id: "sql_server",
      label: "Microsoft SQL Server",
      icon: vendorIcon("sql_server", iconCls),
      description: msg`Enterprise Microsoft`,
    },
    {
      id: "oracle",
      label: "Oracle",
      icon: vendorIcon("oracle", iconCls),
      description: msg`Enterprise legacy`,
    },
    {
      id: "sqlite",
      label: "SQLite",
      icon: vendorIcon("sqlite", iconCls),
      description: msg`File embedded`,
    },
    {
      id: "generic_odbc",
      label: "Generic ODBC",
      icon: vendorIcon("generic_odbc", iconCls),
      description: msg`Qualsiasi DB via DSN`,
    },
  ],

  // ── Steps — one credentials step per vendor (tagged via
  //    ``WizardStepSpec.vendor``). ``ConnectAccountDialog`` selects the
  //    matching step based on the vendor the user picked in
  //    ``AddAccountMenu``. The "host/port" engines share the same shape
  //    (only default port differs) via ``hostPortFields(port)``. ─────────
  steps: [
    {
      id: "creds_postgresql",
      kind: "credentials",
      vendor: "postgresql",
      title: msg`Connessione PostgreSQL`,
      description: msg`Host, database e credenziali del tuo server PostgreSQL.`,
      fields: hostPortFields(5432),
    },
    {
      id: "creds_mysql",
      kind: "credentials",
      vendor: "mysql",
      title: msg`Connessione MySQL`,
      description: msg`Host, database e credenziali del tuo server MySQL.`,
      fields: hostPortFields(3306),
    },
    {
      id: "creds_sql_server",
      kind: "credentials",
      vendor: "sql_server",
      title: msg`Connessione SQL Server`,
      description: msg`Host, database e credenziali del tuo server SQL Server.`,
      fields: hostPortFields(1433),
    },
    {
      id: "creds_oracle",
      kind: "credentials",
      vendor: "oracle",
      title: msg`Connessione Oracle`,
      description: msg`Host, service name e credenziali del tuo server Oracle.`,
      fields: hostPortFields(1521),
    },
    {
      id: "creds_sqlite",
      kind: "credentials",
      vendor: "sqlite",
      title: msg`File SQLite`,
      description: msg`Percorso al file SQLite raggiungibile dall'agente.`,
      fields: sqliteFields,
    },
    {
      id: "creds_odbc",
      kind: "credentials",
      vendor: "generic_odbc",
      title: msg`Connessione ODBC`,
      description: msg`DSN o stringa di connessione ODBC completa.`,
      fields: odbcFields,
    },
  ],

  benefits: [
    {
      icon: <Search className={iconCls} aria-hidden />,
      title: msg`Query in linguaggio naturale`,
      body: msg`Chiedi ad Ando di interrogare i tuoi dati senza scrivere SQL.`,
    },
    {
      icon: <Layers className={iconCls} aria-hidden />,
      title: msg`Schema auto-discovery`,
      body: msg`Ando profila tabelle, colonne e relazioni al primo collegamento.`,
    },
    {
      icon: <Sparkles className={iconCls} aria-hidden />,
      title: msg`Dashboard automatica`,
      body: msg`Grafici e KPI generati dai tuoi dati, pronti per la condivisione.`,
    },
    {
      icon: <RefreshCw className={iconCls} aria-hidden />,
      title: msg`Aggiornamenti incrementali`,
      body: msg`Sincronizzazione delta a basso impatto sul tuo database.`,
    },
  ],

  api: createCapabilityApi<DatabaseConnection>({ capability: "database" }),

  connectionKeyField: "id" as keyof DatabaseConnection,
  getDisplayName: (c) => {
    if (c.file_path) return c.file_path;
    if (c.dsn) return c.dsn;
    if (c.host && c.database) return `${c.host}/${c.database}`;
    return c.id ?? i18nSingleton._(msg`Database`);
  },
  getSecondaryId: (c) => c.connector_type ?? c.vendor ?? null,
};
